import { useContext, useState } from "react";
import { observer } from "mobx-react-lite";
import { Pane, Text, Button, toaster } from "evergreen-ui";

import { PlaylistStoreContext } from "../stores/playlists";
import { beatSaverSongCache } from "../stores/beatSaver";

const customLevelIdPrefix = "custom_level_";

const PlayerDataFileViewer = ({ jsonFile }) => {
  const playlistStore = useContext(PlaylistStoreContext);
  const [loading, setLoading] = useState(false);

  return (
    <div>
      {jsonFile.localPlayers.map((player, idx) => (
        <Pane
          padding="20px"
          key={idx}
          display="flex"
          flexDirection="column"
          border="default"
          borderRadius="5px"
        >
          <Text>
            ID: {player.playerId} | Name: {player.playerName}
          </Text>
          <Button
            marginTop="10px"
            marginBottom="10px"
            isLoading={loading}
            onClick={async () => {
              setLoading(true);
              const playlist = playlistStore.createNewPlaylist();
              playlist.title = "Favourites";

              const songHashes = player.favoritesLevelIds
                .filter((levelId) => levelId.includes(customLevelIdPrefix))
                .map((levelId) =>
                  levelId.substring(customLevelIdPrefix.length, levelId.length)
                );

              // pre-fetch songData
              const songsFetched =
                await beatSaverSongCache.retrieveMultipleSongData(
                  [],
                  songHashes
                );

              songsFetched.forEach((song) => playlist.insertSong(song));
              playlistStore.savePlaylist(playlist);
              setLoading(false);
              toaster.success("Favourites loaded into playlist successfully.");
            }}
          >
            Import favourite levels into playlist
          </Button>
        </Pane>
      ))}
    </div>
  );
};

export default observer(PlayerDataFileViewer);
/*

 {
  version: "2.0.17",
  localPlayers: [
    {
      playerId: "****",
      playerName: "zerc",
      shouldShowTutorialPrompt: false,
      shouldShow360Warning: false,
      agreedToEula: false,
      didSelectLanguage: true,
      agreedToMultiplayerDisclaimer: false,
      avatarCreated: false,
      lastSelectedBeatmapDifficulty: 4,
      lastSelectedBeatmapCharacteristicName: "Standard",
      gameplayModifiers: {
        energyType: 0,
        instaFail: false,
        failOnSaberClash: false,
        enabledObstacleType: 0,
        fastNotes: false,
        strictAngles: false,
        disappearingArrows: false,
        ghostNotes: false,
        noBombs: false,
        songSpeed: 0,
        noArrows: false,
        noFailOn0Energy: false,
        proMode: false,
        zenMode: false,
        smallCubes: false,
      },
      playerSpecificSettings: {
        staticLights: false,
        leftHanded: false,
        playerHeight: 1.6000456809997559,
        automaticPlayerHeight: true,
        sfxVolume: 0.30000001192092898,
        reduceDebris: true,
        noTextsAndHuds: false,
        advancedHud: true,
        saberTrailIntensity: 0.5,
        autoRestart: true,
        noFailEffects: false,
        noteJumpBeatOffset: 0.0,
        hideNoteSpawnEffect: false,
        adaptiveSfx: true,
        environmentEffectsFilterDefaultPreset: 1,
        environmentEffectsFilterExpertPlusPreset: 0,
      },
      practiceSettings: { startSongTime: 0.0, songSpeedMul: 1.0 },
      playerAllOverallStatsData: {
        campaignOverallStatsData: {
          goodCutsCount: 0,
          badCutsCount: 0,
          missedCutsCount: 0,
          totalScore: 0,
          playedLevelsCount: 0,
          cleardLevelsCount: 0,
          failedLevelsCount: 0,
          fullComboCount: 0,
          timePlayed: 0.0,
          handDistanceTravelled: 0,
          cummulativeCutScoreWithoutMultiplier: 0,
        },
        soloFreePlayOverallStatsData: {
          goodCutsCount: 129943,
          badCutsCount: 3603,
          missedCutsCount: 6516,
          totalScore: 29036471,
          playedLevelsCount: 648,
          cleardLevelsCount: 71,
          failedLevelsCount: 421,
          fullComboCount: 0,
          timePlayed: 33310.3828125,
          handDistanceTravelled: 154197,
          cummulativeCutScoreWithoutMultiplier: 12168278,
        },
        partyFreePlayOverallStatsData: {
          goodCutsCount: 0,
          badCutsCount: 0,
          missedCutsCount: 0,
          totalScore: 0,
          playedLevelsCount: 0,
          cleardLevelsCount: 0,
          failedLevelsCount: 0,
          fullComboCount: 0,
          timePlayed: 0.0,
          handDistanceTravelled: 0,
          cummulativeCutScoreWithoutMultiplier: 0,
        },
        onlinePlayOverallStatsData: {
          goodCutsCount: 0,
          badCutsCount: 0,
          missedCutsCount: 0,
          totalScore: 0,
          playedLevelsCount: 0,
          cleardLevelsCount: 0,
          failedLevelsCount: 0,
          fullComboCount: 0,
          timePlayed: 0.0,
          handDistanceTravelled: 0,
          cummulativeCutScoreWithoutMultiplier: 0,
        },
      },
      levelsStatsData: [
        {
          levelId: "100Bills",
          difficulty: 1,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "100Bills",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 162847,
          maxCombo: 77,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "BalearicPumping",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 204929,
          maxCombo: 82,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 7,
        },
        {
          levelId: "Breezer",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 138310,
          maxCombo: 46,
          fullCombo: false,
          maxRank: 3,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "BeatSaber",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 133205,
          maxCombo: 102,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "CommercialPumping",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 202355,
          maxCombo: 93,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "CountryRounds",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 214898,
          maxCombo: 106,
          fullCombo: false,
          maxRank: 5,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "Escape",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 181107,
          maxCombo: 81,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "Legend",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "Legend",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "Escape",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "Escape",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "100Bills",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_D82134351DD101B069632FC5E8A5ED682AE9E65B",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 444213,
          maxCombo: 209,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_9164A5E5F9BC383227DD62ADD402AD2207081320",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 285906,
          maxCombo: 58,
          fullCombo: false,
          maxRank: 3,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "Crystallized",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "Crystallized",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_1D0C762354507CBB9BE926CF6B3DEBC74FCB1B99",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_1D0C762354507CBB9BE926CF6B3DEBC74FCB1B99",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "100Bills",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_1D0C762354507CBB9BE926CF6B3DEBC74FCB1B99",
          difficulty: 1,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_A7F52A8C3C6EE6EB2F9BC89DC6BB7826A1F5039F",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_F5F13EAFF3D6B8220E8366BC5FFFCB02E7BFA2B7",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_072EFB96FD08485DCAB4F19691BFCAC7737DEE3F",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_CCE748EB0958DE0BECBC4679097F5FA3B2F32BA9",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_3EFABA2B1CF80260AB0B9CF504B356FD826C1C06",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_3EFABA2B1CF80260AB0B9CF504B356FD826C1C06",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_F5F13EAFF3D6B8220E8366BC5FFFCB02E7BFA2B7",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 626707,
          maxCombo: 166,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 3,
        },
        {
          levelId: "custom_level_F22061809C798178786CA1B6E61B10CCBCBE8D28",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_A7F52A8C3C6EE6EB2F9BC89DC6BB7826A1F5039F",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_CE0CBE5969CC018FDEDDE53918AC9DACB1004A0C",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "TurnMeOn",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "ExitThisEarthsAtomosphere",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "FitBeat",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "CrabRave",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "BeThereForYou",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_F22061809C798178786CA1B6E61B10CCBCBE8D28",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_6BC768CB64FD97A5019176CC936D7BB8CBA046BC",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_B9E05BAA59F3EA5C97C5AE82766783D895249C61",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_F8509410634644B6544BDD3AC54FDD7453D285AF",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_712EE0801AE2E65E4CD7EA5377BCF272981B57C6",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 412164,
          maxCombo: 189,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_C9C15D3F3109A8F732B41B299CE84556BEBF73AC",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_C9C15D3F3109A8F732B41B299CE84556BEBF73AC",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 297936,
          maxCombo: 168,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 2,
        },
        {
          levelId: "BloodSweatAndTears",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_1B6F9E844D81FF0F6EE6FEED62331A3D229BE2E8",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_580EB7D1614A35D192722F748400C9896AC9527D",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_EBE9CE052C45BA09AF6E6BB1548C3645BE4B9914",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_4D2559D4BE335632AE12BD53360297C6D2D58A90",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 475948,
          maxCombo: 209,
          fullCombo: false,
          maxRank: 5,
          validScore: true,
          playCount: 2,
        },
        {
          levelId: "custom_level_0D662A2BAA501569A4E349FC8AA261C66CAB88FD",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_56F60643F2C9A6E19974F45FE07A46A00CA8B931",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_AD77A5FB3FA53792C341359C07DA841D735C3C71",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_CD5187EAC1C446512FEA587316F6C32B581CDDE5",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_98E299036DE1688C3F60CDA87728EE3C29691888",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_562935328EE367E74D57C96D83C931E0B6991B34",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_26DDC2B3C37AC83CCFB2E5E8B8867598D1BD433D",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_AC00882F01DAF49E59CFCD1D61D7432E7A91EF15",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_76C9218878684411506B4E8F26DD2AA16E4BE6C5",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_EB2B28402E27F784A64F99D338696A2BDD59FE2C",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_3571C983896858E8C02CF2EBD87BD8AB6B3FC237",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_6397E8D54E7DC774EE2236813C936451BEB3EE1C",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_99ED8D1936F5E1C9E74012F6712177B54CF6D86D",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_74D73B73F586B344EB00C23355A88A270B51CD9E",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_7A36E10EC0DE7D1958000459AA6DF59F22C195DE",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_7AFCE73700754A31B92F04D31B8150EA2494801F",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_430317172790D9EC92EDFC8E46991B36EBC1B907",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_A0C1891167FD089A5BC16F4894393780B128E984",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_144E556BE69A33EE67C17594F862044F725C658F",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_EFB4EEA789F717DFCB524DD03DB1E41255D0D661",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_396475EBB44BD7B6663477BE01E0A5D5B69D336F",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_D237761475A881546B0C006C97763DD08126CA66",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_64F703AEC605211A752D068CA9C3177E3519C750",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_ADF7426A6315D9D11BBC9ADB9C3D16F3B8AF10F2",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_1729A28E706231D797389189533CE10E67423B03",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_3A80FCE607711F8E81603943FD30E12DED6FCF32",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_7335439329D4A384606A28B26C3CC4F774F5A6FE",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_AC6EB8BB6CE09461D164DCFE2AE24606BBDFA967",
          difficulty: 4,
          beatmapCharacteristicName: "OneSaber",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_3D3EFFCB84F2BF4AADCD7F6BA7F89EB37226A3C3",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_CFBAF5F8676E95A2627FFB7B5F09C915FDEF91D2",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_AD857BC882FCD392B8CFE4A03DB112332E9B7CEA",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_16EE07235B8DB234CA1900532FF7EF17E6F6111A",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_00B539E86EA3F0B1E966544A297FB4942E499E6F",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_D0B80C6F3B072791779D39A2BC637719B6E99250",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_1CCA456BF71339F4A9B0E7BEE5EF9E6A016D20CC",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_727F16F8BEF6DDB62F7A1ABD3677737F5082A302",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_B54881C288E83036EC8A67E774926CE334798F4E",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_4B7CDFCF558F66ACC4B7A506C2DD50FBD9A76354",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_504081C2C29B1E56BE2EC7C30E368EC2C433E251",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_1C48C42297CC83857C555C3FDF66E0B47F9062DE",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_E46F9F4F48D0A33F7117530E2402FD1F8843BC11",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_8E834060FE521D85009BFD8BF4AA126C40AEB294",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_60EBE250E7D3E1DDCD5C79261855EF45CAB0C577",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_D54DFB96712B16D717171454F04B5CB050BF134A",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_283D35198503DE65FBC45905B22CE1D64FDEA57D",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_A603656EB8CDADCB6EDA85EF408902C1F6125A8F",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_3D0AA2B24427A62E8E5E78F8275AF9539029B919",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_16ED4D63AB196F5ED70EAE6EFE197439258286F1",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 433415,
          maxCombo: 473,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 2,
        },
        {
          levelId: "custom_level_E33EA6F7E5083EC4DB00BEC2A27FA34DF6BEF448",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_121FBAB89F9C7FB9F87B87B0B75E04ED850A14F3",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 788246,
          maxCombo: 164,
          fullCombo: false,
          maxRank: 5,
          validScore: true,
          playCount: 4,
        },
        {
          levelId: "custom_level_60E0C5ED18C8BDE3262E1627B282B9F4C5027C8C",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_6B811483EEC8F242F650599B82168AA5FE306F6A",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_F883CDF6CC0F82CFF05B66C896C7908E3B71DC3C",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 375916,
          maxCombo: 63,
          fullCombo: false,
          maxRank: 3,
          validScore: true,
          playCount: 2,
        },
        {
          levelId: "custom_level_4F0EF62B9460D5D41BF235CA7ABF49A1140C9BAE",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_0F1467CF627A9B8CCA54985FFE95670CC39201BE",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_158C9568224208C43460FD10BB058F55B419825E",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_1471A907148A540FCD567152FE9A5CDADD18C3DE",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_CF72005F847D4064BC82C1C9F81DECDC300E0EC6",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_56AFB126C31A5191F2D6EB195811E80CE1899724",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_222ED70CD7B4CDA89363943DA9707F160140BBD4",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 713139,
          maxCombo: 170,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 3,
        },
        {
          levelId: "custom_level_DAA9DD555E08EE928F4E0E5A04B091310BC9B74B",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_6E74503A2C4B2F03B3BEE874DBEDA9789A3CD5F9",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_ECF0DF1A5D53E42BBAA1A992B9B16D4A979838C8",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_E4FEED6FB1C485361BDC23E7BE5A47A588661B40",
          difficulty: 0,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_60798E61122BDE7949AB5DAD8E91ACDD4581BD4C",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_7255B75C48C1D697E7C7FD18BAEFE24378F7218A",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_AA2F7BF0DF25CD57DDDAC159FA7C159F732E0553",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 282271,
          maxCombo: 150,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_1DC9F6A833FD1706BAA0901B714AA534FED62953",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_2C002D2874E029DB43F3C7CF9BB271AE0D769B74",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_359433E3D701CC06B47AC0D7FBB9B077CD970C01",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_0A9EFE27DAF5C1ACD8B17F7557FA3AD2A36AECE2",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_C1C8E2B9394050AFAD435608137941DA0B64B8F3",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 244592,
          maxCombo: 111,
          fullCombo: false,
          maxRank: 5,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_D70320C6008E0AAD89507916A6DFD72549EFD230",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_D99BABBF5F2C54799D6FFAC241ECF935A4E6DCD1",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_9B3C1C1B942D94CC1528C27BC8C6C6E0F3E9CED6",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_FB79E15FD5CE8DF9409F98B2F78D2349F8D00D5C",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_A2C21A3A478F14422185F77CC8F9030F638FCF06",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_D7CE21C61209662ED5592F5BD5FA2D43A80E24D2",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 668234,
          maxCombo: 144,
          fullCombo: false,
          maxRank: 1,
          validScore: true,
          playCount: 4,
        },
        {
          levelId: "custom_level_44DCABFA3A44E450BC60EBCCA18552B09A04F5DC",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_28CA52614B05C4D1D4700402CC9B69B9401029AA",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_3911F07D3AE1BB1B881D5D56B7312ED15322EE74",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_A2B52987107A01E0C08734711CDE552B534DFC4B",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_90D0104CE0F496A275B7F21FB05BD4609766C0B1",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_4C5CBD544C60DC51EF8DC4B93D7E1D4910DDE81F",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_15ACFB805C96FE470EC2B3F27214A870E4B97330",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_8B176DE1E1CDAC314746A70E5B1F62847A7A12F2",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 172387,
          maxCombo: 177,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_4F4087E1CCE3CE26227FE5EE6F1E4E292582B31C",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 347418,
          maxCombo: 159,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_261505CF27EB6CB1D1CBC2187783027114C36196",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_2E732B75E97D000E0277577236D74A3709565624",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_AD2FD5CB36B1976C6030937405066D8B81E03897",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_34D796FFE405565FDCFB8AB819B79E48E71E6DE9",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_9CCDE4393ECAA7911E989A42408F5DB9DFA22E6B",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_42BF481E493B33999FE90D241ED72C5C04449F40",
          difficulty: 1,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_8B8C6AF9F78594D11800FCBBBF6EF95E4DFAADB5",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_15B274F457A60106CB8FC64A75163361CD1C054F",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_A3E151D3C4F083606A3814F9BD76EBD2CCBD8694",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_91F4D2B3FE0F62F28591D7FE501CABE3E2501E1F",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_2A64D6D475F848ED5C748DDAC6438DA92D9949E6",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_1F06AE3ACBA16C27171EBA05016ABEB3B9621533",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_3B3EF5F5438E7928FDB747004308E8A8D6A23DC8",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_51835F468381D630B48C36DA70D2C2CE700BB274",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_8D9DF176C94ECF5C7725457525B6F959F5EF9296",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 273221,
          maxCombo: 201,
          fullCombo: false,
          maxRank: 5,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_0BC6562A0BDE3A933D656A514C67EEEC88A315DF",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_D55C6AEA97FC85CD439C460EBA0D2CEB2C7B51FA",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_8C392C31AEADB55EEBEB978C09FA79857380F15C",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_1DFF4985E670F048EEEE32E6B3D9CADE393F678F",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_A2CB9EB1E2908EE63FD3EF25F1986D2AA0DF82FE",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_F12373ABAFD0072A135EB3A71EF4C1138323D67E",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 294937,
          maxCombo: 83,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_F346E07023EFB3C05ED61E80879409BF602BDD59",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 214616,
          maxCombo: 168,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_FE57F78FCE9AA698E205106755287B4CF01724B2",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 316513,
          maxCombo: 197,
          fullCombo: false,
          maxRank: 5,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_419E5858E5BC1BE203B7059C9B519FF5195A374F",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_2E540F037C84BE9CDF5A233A750986ED2B29ACE0",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_4BC0889DD07F24ACC359322C1821704D477EDE44",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_430E64463167A9B788B103CF473255CC1C4D9D8B",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_A70FE61A1A9123470C0BAEB09919EC3CBBF54D45",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_FDBB4704AE6E379A295E8239069EBF56BF1BAD37",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_FDBB4704AE6E379A295E8239069EBF56BF1BAD37",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_FDBB4704AE6E379A295E8239069EBF56BF1BAD37",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 94434,
          maxCombo: 81,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_4F0E4AFB0013272CC67066D7A3A034FC2E3B22EC",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_33D5C4FFF97822EEF1F02FC53BB0561F23489EFE",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_C39C719A17A081D0BD0806052BD39BA4EE39B6B0",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_9A1D001995CC0A2014352AA7148CBCBF2E489D89",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_AA82FCD0AAA656B6A00639B3618E7E665F19C557",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_7494A7CFD0A9CC5D029A35B52EECC2EFCA2ED991",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_D7B7EFDEC77D3A5032C2CEB0E66FE33C6AF97C66",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_F8509410634644B6544BDD3AC54FDD7453D285AF",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_95E11F39B2898A38E3B5E61D2156EF5F5835F40F",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 282985,
          maxCombo: 138,
          fullCombo: false,
          maxRank: 5,
          validScore: true,
          playCount: 3,
        },
        {
          levelId: "custom_level_FA462260BF92D304455D59215452027221B64BE7",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_30EF2E8690DEC1F1B8B821C33FA5789FD7E0A1C6",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_8D7D7366320A5F353258396F79FC59413F013562",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_6A25A31FB5F392150DAF12E34417E131CD5ECDE2",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_AD77A5FB3FA53792C341359C07DA841D735C3C71",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_4F4741EC41D4282EC2CD4894EBF5E902C1ECE57E",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_562935328EE367E74D57C96D83C931E0B6991B34",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_AC00882F01DAF49E59CFCD1D61D7432E7A91EF15",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_8B8C6AF9F78594D11800FCBBBF6EF95E4DFAADB5",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_799662876B57DCF7A55B5B91BD29BBFD46F10C30",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_46F95A01988F37CF1B0C32AE0DDD4C7E50D00273",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_C426950CDB667E5A3FAB40BB2438F0A316B7BBA5",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_BBBE913B11CC84C2E7B1FCAD75DF78AEFF000791",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 377245,
          maxCombo: 94,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_EED61B476504B800AD9C1EE61A6505E3105FB8AD",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_EF4216E1CD5208A82FA04C911E4A28DA3EB7E5DD",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_2E687B4E357C8FB14D1CBA11B3D4029070ED51F7",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_7681DEF9E1BE13FEB2B21D150FCF5E19ADB40988",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_01CF007F0C2AF6B2DDE958780F2BD2125BBD5F60",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_BABAB629DC74B25731386914BD1DB2B0F76E8A5A",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_33D5C4FFF97822EEF1F02FC53BB0561F23489EFE",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 445925,
          maxCombo: 119,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_C5B3F2EBA33F96F9D0E052F02768898134CA2109",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_F1EC6967A2A3940C2E65FCB207FC418F2813403D",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_BB2BD82208D452BADC4ACAF877DC2C4ABD0FAB15",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_2738E2374B821B6AA9839F128A70CAAE8333CB1C",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_C9C7B1B8B751D5C1409C7F07562B1A48D177895B",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_CAF27CB036508502F36FDFEA39022EE18D0BCAC0",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_310694F2FF8D129D4E64192251653CAFFDC65B62",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_EDF637F016AAFD091B9B1ACE8A0F093FC137B0FF",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_6DD531F3920735B2B1B64F39442FCB753B4A52A7",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_24D1346697DFEC8178F0568A77D6C2325EDA9367",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_D00FFAA223C296C4876415CE563976D5E622900B",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_BA9D1B23845240F297CD779CD784D8375FF2CA94",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_A06D1569B8A0A4635BFCD6C7F1AEE348DF9A22C8",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_11E5E69AA6AA4C623952EC0C1349038E942DDAD1",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_5536BE9C26867AB38524FA53E30FC1AB889D3251",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_512A79160F9F0C37B32B856BE3B24F9109448608",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_D3A3A5C2C1F5B421F995644C188D35E4B5B58443",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 623521,
          maxCombo: 243,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_A0E38EE68C0CB89FA4E305D3696EDB56222A2E63",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "Escape",
          difficulty: 3,
          beatmapCharacteristicName: "OneSaber",
          highScore: 227859,
          maxCombo: 227,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "ExitThisEarthsAtomosphere",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "LightItUp",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "FitBeat",
          difficulty: 3,
          beatmapCharacteristicName: "OneSaber",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "CrabRave",
          difficulty: 3,
          beatmapCharacteristicName: "OneSaber",
          highScore: 302744,
          maxCombo: 86,
          fullCombo: false,
          maxRank: 3,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "100Bills",
          difficulty: 3,
          beatmapCharacteristicName: "OneSaber",
          highScore: 194629,
          maxCombo: 158,
          fullCombo: false,
          maxRank: 3,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "BalearicPumping",
          difficulty: 3,
          beatmapCharacteristicName: "OneSaber",
          highScore: 291819,
          maxCombo: 141,
          fullCombo: false,
          maxRank: 3,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "BeatSaber",
          difficulty: 3,
          beatmapCharacteristicName: "OneSaber",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "Legend",
          difficulty: 3,
          beatmapCharacteristicName: "OneSaber",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "TurnMeOn",
          difficulty: 3,
          beatmapCharacteristicName: "OneSaber",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "BeThereForYou",
          difficulty: 3,
          beatmapCharacteristicName: "OneSaber",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "Elixia",
          difficulty: 3,
          beatmapCharacteristicName: "OneSaber",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "INeedYou",
          difficulty: 3,
          beatmapCharacteristicName: "OneSaber",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "RumNBass",
          difficulty: 3,
          beatmapCharacteristicName: "OneSaber",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "UnlimitedPower",
          difficulty: 3,
          beatmapCharacteristicName: "OneSaber",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "UnlimitedPower",
          difficulty: 4,
          beatmapCharacteristicName: "OneSaber",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "CommercialPumping",
          difficulty: 3,
          beatmapCharacteristicName: "OneSaber",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "Origins",
          difficulty: 4,
          beatmapCharacteristicName: "OneSaber",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "ReasonForLiving",
          difficulty: 4,
          beatmapCharacteristicName: "OneSaber",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "GiveALittleLove",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "FullCharge",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "BurningSands",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "CrabRave",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_28026763533539157B41B86EF49F9AAB49FB8F5C",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_4BA6575E63038CC8BB747F7C1E56BF2202D551BE",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_DEB9105512C5E70C68C57517EA7F423C360DDB97",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_236173D5BA7DC379D480B9CB5FB6B4FA5ABE77DA",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_0CF2AA6E7FF8465AEB12FAD2B55EDD58416052C6",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_9C316132301925E301523EB22F025AF7115429D3",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_E7556DBCB0978864DEDAE1911314B78C78157993",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_E7556DBCB0978864DEDAE1911314B78C78157993",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_DDC59E53EBDC3361AA8AFF41040ECBED513D7D9D",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_60EBE250E7D3E1DDCD5C79261855EF45CAB0C577",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_F947847D309EACDE0205240A1C8F8BC35C23E057",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_9393063F36241DE74EB9A65B76B916A79709498E",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_C1B08E5C05C9E6DAD4096F3FF04D1E0497A0A339",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 560007,
          maxCombo: 279,
          fullCombo: false,
          maxRank: 5,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_A2F3A43FE60DDF86EA01D715C423619F01AE743C",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_831247D7D02E948E5D03622748BB130B5057023D",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_1D0C868A313C39441ECA31A32380BD0909D7C095",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "FitBeat",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_FFF756F6D76BD0049082D125773B6EC97C28C89B",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_022C187AE58A1897BBC309B4F6B62ECB4ADC5760",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_5422D364080D5C1B7046359E31A6C61514B91FEB",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_C46AE0BAB534E92A51082913F55201B523B6997E",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_A854C67C4F1FEA217755C5F23D4A15EEBBD73935",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_D217D9CD69D8EE1A1C5D2B0E8EE54E56B32B0937",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_A2C9BB878C22C1BF52B8AD4EA144C5AF87846DF9",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_A26088301307344C5602120323F7DABCF3C1DC25",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_FE7EAD7BF2F13571C8F6123677B92B08A99376F6",
          difficulty: 4,
          beatmapCharacteristicName: "360Degree",
          highScore: 224451,
          maxCombo: 141,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_F4AFD3082E2B66CC4F682F1E115FC0D7718A66EA",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_470F2DAFC6BC4045E6F996128F0BEB2C661BB476",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_F6FDBDCD51E38A86DCBFD028BC84FF0301D439AA",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 283174,
          maxCombo: 121,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_74CDF5D9D53D5530B0D34233DC36C298BE436885",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_74CDF5D9D53D5530B0D34233DC36C298BE436885",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_1B6F9E844D81FF0F6EE6FEED62331A3D229BE2E8",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_2B1EF0C0882267CFF4A16793763D58F6CFFC6720",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_144E556BE69A33EE67C17594F862044F725C658F",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_768776B15AFC801F67A2244439938740349CBC74",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_D77C3FCDE7E7672405809D22C78B5EA1AE900283",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_2A1F7DFBD9C59B22D4A8E847B0730872DD1CF759",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_51835F468381D630B48C36DA70D2C2CE700BB274",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_741B8942F381DBA3739BA820345D0918C121714B",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_E517DC73E5390A75917A10A76C1CC0198DAC5910",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_E68746D4DEF4FD9D0149ADC16E858AF9B0E46BE5",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_4D7B9CDA26DE1117CB5935CEE18C4A50D5402207",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_41325CA37B5D13E13835DAB724C0E00D13FD4FE5",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_DC4906A7F76965CDD2B75C72CF470344B698E352",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_4D37F92C0C52B6D71E77132D3E2B84B153514F55",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 320077,
          maxCombo: 93,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 2,
        },
        {
          levelId: "custom_level_EF4216E1CD5208A82FA04C911E4A28DA3EB7E5DD",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_620E7078EF66497194AC2F74D90003FD47C0758B",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_BAEED9579EE22718840B5A253A485410DD0171B9",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_3E86DAF870FB28706C91DF792272066F3F396A2F",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_EFB4EEA789F717DFCB524DD03DB1E41255D0D661",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_4971CA2DF3798DA933745DD3B1C558EE5ED079A6",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_DB4F35F2F1020255211E74A084D13F59371C8A57",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 142910,
          maxCombo: 71,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_A7FE2E5DAD8AC45271A5FA64F964A31352B7C31D",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_C35FC730B094498F60458F5565CE20553C86C150",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_6B811483EEC8F242F650599B82168AA5FE306F6A",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_0F1467CF627A9B8CCA54985FFE95670CC39201BE",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_2ED593834429C153F8B3CB2FCFD331400890A05E",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_2ED593834429C153F8B3CB2FCFD331400890A05E",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_C431DCE3828958477D297DA64BD2536E3BFE60EA",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_C431DCE3828958477D297DA64BD2536E3BFE60EA",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_B54BA3FE785F504F9E2C9B9125157873E14EA922",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_B54BA3FE785F504F9E2C9B9125157873E14EA922",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_35DAF60CBD134FDF92887D5B693409EFAAA4D03A",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_FFA7F6A747EE9F4C3C48CEC56D511B9CFF46B2C6",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_F39DD075F55C5B5D0383837F4EF4F5FEB7817BE8",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_DC5B8A8F9178F2978521A942A7CAF391712C63F9",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_DD2AD497E28BE294E6C4BCB9275992CE232CED93",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_4827D1733F37278FC83F9DA5A2F8DD0715248677",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_A5CE7EB84407C9683533C7F84143B7C672D4E1E0",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_4975C6D3FD81C353A44216A9B9F702B025EF56EA",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_A03FA0DA66E4AC650D5888884D5BF885249B1C0E",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_27E8E84943AD8CF2B887FACDB034BCAB01B9D877",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_F931E1D23E0FAD3B1AC22D86C55AB17718470B6D",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_65D95E1076BB53D368F64B556C9BF6C05226B356",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_2BAB62E7CDF086EC550411CA3DF5BC3265B5FC11",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_92D646777B37B85E2F6D37CE953F6B07D3248631",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 484645,
          maxCombo: 186,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_8E6B2149B65744CEF77C8928DAB1A3F1843FE3DD",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 284048,
          maxCombo: 106,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_52BAD8401DBC5AE968B8E2A59141C64646917FA1",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_122D07B5950B56E5973895BFA8E067C6474A7C06",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_FE387C4C7B03326139A5144ED21C4A02774B5DD9",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_84D8B2DFC4A4D1AFB22E5DBB0708D891A95BEF0F",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 887392,
          maxCombo: 149,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 4,
        },
        {
          levelId: "custom_level_F837FE9E1A313D557CB1CEB6F38CBBBB1E2CCA66",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 644556,
          maxCombo: 304,
          fullCombo: false,
          maxRank: 5,
          validScore: true,
          playCount: 2,
        },
        {
          levelId: "BeatSaber",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_CBB15D6D81C7FA0848ED3A60330A363E57DECD21",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 246152,
          maxCombo: 128,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_C8407128E436DC9E1B322ADE23206A74C81676C0",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_0F6F202776ABD22D83D84BF6163E4E6331DDF853",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_144E556BE69A33EE67C17594F862044F725C658F",
          difficulty: 2,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_A5CE7EB84407C9683533C7F84143B7C672D4E1E0",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 333748,
          maxCombo: 155,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_222ED70CD7B4CDA89363943DA9707F160140BBD4",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_1CCA456BF71339F4A9B0E7BEE5EF9E6A016D20CC",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_D0B80C6F3B072791779D39A2BC637719B6E99250",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_56F60643F2C9A6E19974F45FE07A46A00CA8B931",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_69311E31B423637EC26A99770059ACC542949233",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 400025,
          maxCombo: 96,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "Origins",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "IntoTheDream",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "LightItUp",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_61725FEC2E41A7A32F527B2A658A12614E3F6380",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_E8FD63D205DB98E63C27B6CC902CE6CACFD4CF92",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_CB3069AEEF3D13478F2EE5321A18E35E673C9691",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_E3B92215E9EEDB1ED968842716DC292DFB59F7A0",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_324F7770069D31F9556C323058A52DA2EE0D7A9C",
          difficulty: 0,
          beatmapCharacteristicName: "Standard",
          highScore: 9991,
          maxCombo: 21,
          fullCombo: false,
          maxRank: 3,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_8E7E553099436AF31564ADF1977A5EC42A61CFFF",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_0F161DF8682F4141A45A9FE183636DF9A859B824",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_7C17869EAFC8412150B22BE143F78CF490BD693B",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_C4B7E985750E86ADF1D0C94ED4B0557ED18685E2",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 613563,
          maxCombo: 75,
          fullCombo: false,
          maxRank: 4,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "custom_level_73D4F2C694CB0E9781EFDDD525D402C445B7B08B",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
        {
          levelId: "custom_level_5A9DC19CB5D8E353153B02A1A886123861514A2D",
          difficulty: 3,
          beatmapCharacteristicName: "Standard",
          highScore: 185947,
          maxCombo: 148,
          fullCombo: false,
          maxRank: 5,
          validScore: true,
          playCount: 1,
        },
        {
          levelId: "CycleHit",
          difficulty: 4,
          beatmapCharacteristicName: "Standard",
          highScore: 0,
          maxCombo: 0,
          fullCombo: false,
          maxRank: 0,
          validScore: false,
          playCount: 0,
        },
      ],
      missionsStatsData: [
        { missionId: "1", cleared: false },
        { missionId: "2a", cleared: false },
        { missionId: "3", cleared: false },
        { missionId: "4b", cleared: false },
        { missionId: "5a", cleared: false },
        { missionId: "6b", cleared: false },
        { missionId: "7a", cleared: false },
        { missionId: "8a", cleared: false },
        { missionId: "11", cleared: false },
        { missionId: "12a", cleared: false },
        { missionId: "12b", cleared: false },
        { missionId: "13b", cleared: false },
        { missionId: "14b", cleared: false },
        { missionId: "15", cleared: false },
        { missionId: "16a", cleared: false },
        { missionId: "17a", cleared: false },
        { missionId: "18", cleared: false },
        { missionId: "19a", cleared: false },
        { missionId: "20a", cleared: false },
        { missionId: "21", cleared: false },
        { missionId: "22a", cleared: false },
        { missionId: "23b", cleared: false },
        { missionId: "24b", cleared: false },
        { missionId: "26", cleared: false },
        { missionId: "27", cleared: false },
        { missionId: "28b", cleared: false },
        { missionId: "28a", cleared: false },
        { missionId: "29", cleared: false },
        { missionId: "30", cleared: false },
        { missionId: "31", cleared: false },
        { missionId: "24c", cleared: false },
        { missionId: "25", cleared: false },
        { missionId: "23a", cleared: false },
        { missionId: "24a", cleared: false },
        { missionId: "22b", cleared: false },
        { missionId: "19b", cleared: false },
        { missionId: "20b", cleared: false },
        { missionId: "17b", cleared: false },
        { missionId: "16b", cleared: false },
        { missionId: "14a", cleared: false },
        { missionId: "13a", cleared: false },
        { missionId: "12c", cleared: false },
        { missionId: "7b", cleared: false },
        { missionId: "8b", cleared: false },
        { missionId: "9b", cleared: false },
        { missionId: "10", cleared: false },
        { missionId: "9a", cleared: false },
        { missionId: "6a", cleared: false },
        { missionId: "5b", cleared: false },
        { missionId: "4a", cleared: false },
        { missionId: "2b", cleared: false },
      ],
      showedMissionHelpIds: [],
      colorSchemesSettings: {
        overrideDefaultColors: false,
        selectedColorSchemeId: "User0",
        colorSchemes: [
          {
            colorSchemeId: "User0",
            saberAColor: {
              r: 0.658823549747467,
              g: 0.125490203499794,
              b: 0.125490203499794,
              a: 1.0,
            },
            saberBColor: {
              r: 0.125490203499794,
              g: 0.3921568691730499,
              b: 0.658823549747467,
              a: 1.0,
            },
            environmentColor0: {
              r: 0.7529411911964417,
              g: 0.1882352977991104,
              b: 0.1882352977991104,
              a: 1.0,
            },
            environmentColor1: {
              r: 0.1882352977991104,
              g: 0.5960784554481506,
              b: 1.0,
              a: 1.0,
            },
            obstaclesColor: {
              r: 1.0,
              g: 0.1882353127002716,
              b: 0.1882353127002716,
              a: 1.0,
            },
          },
          {
            colorSchemeId: "User1",
            saberAColor: {
              r: 0.658823549747467,
              g: 0.125490203499794,
              b: 0.125490203499794,
              a: 1.0,
            },
            saberBColor: {
              r: 0.125490203499794,
              g: 0.3921568691730499,
              b: 0.658823549747467,
              a: 1.0,
            },
            environmentColor0: {
              r: 0.7529411911964417,
              g: 0.1882352977991104,
              b: 0.1882352977991104,
              a: 1.0,
            },
            environmentColor1: {
              r: 0.1882352977991104,
              g: 0.5960784554481506,
              b: 1.0,
              a: 1.0,
            },
            obstaclesColor: {
              r: 1.0,
              g: 0.1882353127002716,
              b: 0.1882353127002716,
              a: 1.0,
            },
          },
          {
            colorSchemeId: "User2",
            saberAColor: {
              r: 0.658823549747467,
              g: 0.125490203499794,
              b: 0.125490203499794,
              a: 1.0,
            },
            saberBColor: {
              r: 0.125490203499794,
              g: 0.3921568691730499,
              b: 0.658823549747467,
              a: 1.0,
            },
            environmentColor0: {
              r: 0.7529411911964417,
              g: 0.1882352977991104,
              b: 0.1882352977991104,
              a: 1.0,
            },
            environmentColor1: {
              r: 0.1882352977991104,
              g: 0.5960784554481506,
              b: 1.0,
              a: 1.0,
            },
            obstaclesColor: {
              r: 1.0,
              g: 0.1882353127002716,
              b: 0.1882353127002716,
              a: 1.0,
            },
          },
          {
            colorSchemeId: "User3",
            saberAColor: {
              r: 0.658823549747467,
              g: 0.125490203499794,
              b: 0.125490203499794,
              a: 1.0,
            },
            saberBColor: {
              r: 0.125490203499794,
              g: 0.3921568691730499,
              b: 0.658823549747467,
              a: 1.0,
            },
            environmentColor0: {
              r: 0.7529411911964417,
              g: 0.1882352977991104,
              b: 0.1882352977991104,
              a: 1.0,
            },
            environmentColor1: {
              r: 0.1882352977991104,
              g: 0.5960784554481506,
              b: 1.0,
              a: 1.0,
            },
            obstaclesColor: {
              r: 1.0,
              g: 0.1882353127002716,
              b: 0.1882353127002716,
              a: 1.0,
            },
          },
        ],
      },
      overrideEnvironmentSettings: {
        overrideEnvironments: false,
        overrideNormalEnvironmentName: "DefaultEnvironment",
        override360EnvironmentName: "GlassDesertEnvironment",
      },
      favoritesLevelIds: [
        "Crystallized",
        "custom_level_74CDF5D9D53D5530B0D34233DC36C298BE436885",
        "custom_level_84D8B2DFC4A4D1AFB22E5DBB0708D891A95BEF0F",
        "custom_level_F5F13EAFF3D6B8220E8366BC5FFFCB02E7BFA2B7",
      ],
      multiplayerModeSettings: {
        createServerNumberOfPlayers: 5,
        quickPlayDifficulty: "Easy",
        quickPlaySongPackMask: [],
        quickPlaySongPackMaskSerializedName: "BUILT_IN_LEVEL_PACKS",
      },
    },
  ],
  guestPlayers: [],
};
*/
